// src/components/customers/DoorWorkInfo.jsx

/**
 * DoorWorkInfo Component
 *
 * Displays information about door work requirements for a job.
 * Shows door cutting needs and door plate requirements with visual indicators.
 *
 * @param {Object} props - Component props
 * @param {Object} props.job - Job object containing door work information
 * @param {number} props.job.doorsNeedCutting - Number of doors that need cutting
 * @param {number} props.job.numberOfDoorPlateNeeded - Number of door plates needed
 * @param {string} props.className - Additional CSS classes
 */
const DoorWorkInfo = ({ job, className = "" }) => {
  const { doorsNeedCutting = 0, numberOfDoorPlateNeeded = 0 } = job || {};

  // Don't render if no door work is required
  if (doorsNeedCutting === 0 && numberOfDoorPlateNeeded === 0) {
    return (
      <div
        className={`bg-gray-50 rounded-lg p-4 border border-gray-200 ${className}`}
      >
        <div className="flex items-center text-gray-600">
          <svg
            className="w-5 h-5 mr-2 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className="text-sm font-medium">No door work required</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg border border-gray-200 ${className}`}>
      {/* Header */}
      <div className="flex items-center p-4 border-b border-gray-200 bg-orange-50">
        <div className="flex items-center">
          <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center mr-3">
            <svg
              className="w-5 h-5 text-orange-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Door Work Required
            </h3>
            <p className="text-sm text-gray-600">
              Cutting and hardware requirements
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Doors to Cut */}
          <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center mr-3">
                  <svg
                    className="w-5 h-5 text-amber-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    Doors to Cut
                  </p>
                  <p className="text-xs text-gray-500">
                    Doors requiring adjustment
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-amber-600">
                  {doorsNeedCutting}
                </p>
                <p className="text-xs text-gray-500">
                  {doorsNeedCutting === 1 ? "door" : "doors"}
                </p>
              </div>
            </div>

            {doorsNeedCutting > 0 && (
              <div className="mt-3 pt-3 border-t border-amber-200">
                <div className="flex items-center text-amber-700">
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                  <span className="text-xs font-medium">
                    Cutting work required
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Door Plates Needed */}
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                  <svg
                    className="w-5 h-5 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    Door Plates
                  </p>
                  <p className="text-xs text-gray-500">Hardware requirements</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-blue-600">
                  {numberOfDoorPlateNeeded}
                </p>
                <p className="text-xs text-gray-500">
                  {numberOfDoorPlateNeeded === 1 ? "plate" : "plates"}
                </p>
              </div>
            </div>

            {numberOfDoorPlateNeeded > 0 && (
              <div className="mt-3 pt-3 border-t border-blue-200">
                <div className="flex items-center text-blue-700">
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                    />
                  </svg>
                  <span className="text-xs font-medium">Hardware to order</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Summary */}
        {(doorsNeedCutting > 0 || numberOfDoorPlateNeeded > 0) && (
          <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-start">
              <svg
                className="w-5 h-5 text-gray-500 mr-2 mt-0.5 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="text-sm text-gray-700">
                <p className="font-medium mb-1">Door Work Summary</p>
                <ul className="space-y-1 text-xs">
                  {doorsNeedCutting > 0 && (
                    <li>
                      • {doorsNeedCutting}{" "}
                      {doorsNeedCutting === 1 ? "door" : "doors"} require
                      cutting for proper fit
                    </li>
                  )}
                  {numberOfDoorPlateNeeded > 0 && (
                    <li>
                      • {numberOfDoorPlateNeeded} door{" "}
                      {numberOfDoorPlateNeeded === 1 ? "plate" : "plates"}{" "}
                      needed for hardware
                    </li>
                  )}
                  <li>
                    • Plan additional time for door work during installation
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DoorWorkInfo;
